import React, { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import "./index.css";

// Import debug utility for staging environment
import "./utils/environment-debug.ts";

// Simplified main.tsx for staging - focus on getting React to load
console.log("🚀 Starting React application...");

const rootElement = document.getElementById("root")!;

// Check if the app is pre-rendered (has SSR content)
const hasPrerenderedContent = rootElement.children.length > 0;

console.log("📦 React version:", React.version);
console.log("🎯 Root element found:", !!rootElement);
console.log("🔍 Has prerendered content:", hasPrerenderedContent);

try {
  if (hasPrerenderedContent) {
    // Hydrate the pre-rendered content
    console.log("🔄 Hydrating pre-rendered content...");
    hydrateRoot(
      rootElement,
      <StrictMode>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </StrictMode>,
    );
  } else {
    // Create new root for client-side rendering
    console.log("🆕 Creating new root for client-side rendering...");
    createRoot(rootElement).render(
      <StrictMode>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </StrictMode>,
    );
  }
  console.log("✅ React app rendered successfully!");
} catch (error) {
  console.error("❌ Error rendering React app:", error);
}

// Handle service worker for staging vs production
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  if (window.location.hostname.includes("staging")) {
    // Force unregister service worker for staging to avoid caching issues
    console.log(
      "🔄 Staging environment detected - unregistering service worker",
    );

    // Immediately unregister any existing service worker
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        console.log("🗑️ Unregistering service worker:", registration);
        registration.unregister();
      });
    });

    // Clear all caches immediately
    if ("caches" in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          console.log("🗑️ Clearing cache:", cacheName);
          caches.delete(cacheName);
        });
      });
    }

    // Force reload if service worker was active
    if (navigator.serviceWorker.controller) {
      console.log("🔄 Service worker was active - forcing reload");
      window.location.reload();
    }
  } else {
    // Register service worker for production
    serviceWorkerRegistration.register();
  }
}
